// Store
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetSearchInfo } from "store/slice/actions";
import { customAsyncThunk } from "store/slice/slices.functions";

// Store
import { RootState } from "app/store";

// Types
import { RequestInfo } from "store/slice/store.types";
import { ProjectCompanySet } from "services/projects/project.types";
import { fetchProjectByIdService } from "services/projects/project.service";
import { differenceBy } from "lodash";

type TCompanySetSlice = {
    status: RequestInfo;
    data: ProjectCompanySet[];
};

const initialState: TCompanySetSlice = {
    status: "pristine",
    data: [] as ProjectCompanySet[],
};

export const fetchCompanySet = customAsyncThunk<ProjectCompanySet, { projectId: number | string; path: string }>(
    "project/CompanySetList/getCompanySet",
    async ({ projectId, path }, { getState }) => {
        const {
            project: {
                companiesSet: { data: companySetList },
            },
        } = getState();

        const companySet = companySetList.find((set) => set.meta.path === path);

        if (companySet) {
            return companySet;
        } else {
            const { data } = await fetchProjectByIdService<ProjectCompanySet>(Number(projectId), path, {
                exclude_clones: false,
                exclude_company_set_companies: false,
            });
            return data;
        }
    }
);

const companySetSlice = createSlice({
    name: "project/CompanySetList",
    initialState,
    reducers: {
        addCompanySet: (state, action: PayloadAction<ProjectCompanySet[] | undefined>) => {
            const existingCompaniesSet = state.data;
            const newSets = action.payload || [];
            const newValues = differenceBy(newSets, existingCompaniesSet, "data.id");
            return { ...state, data: [...state.data, ...newValues] };
        },
        updateCompanySet: (state, action: PayloadAction<ProjectCompanySet>) => {
            const id = action.payload.data.id;
            const companySets = state.data.map((companySet) => {
                return companySet.data.id === id ? action.payload : companySet;
            });
            return { ...state, data: companySets };
        },
        clearInsightsData: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCompanySet.pending, (state) => ({ ...state, status: "fetching" }));
        builder.addCase(fetchCompanySet.rejected, (state) => ({ ...state, status: "error" }));
        builder.addCase(fetchCompanySet.fulfilled, (state, action) => ({
            ...state,
            data: state.data.find((d) => d.meta.path === action.payload.meta.path)
                ? state.data
                : [...state.data, action.payload],
            status: "pristine",
        }));
        builder.addCase(resetSearchInfo, () => initialState);
    },
});

export const { addCompanySet, updateCompanySet } = companySetSlice.actions;

export const getCompanySets = (store: RootState) => store.project.companiesSet;
export const getCompanySetByPath = (companySetPath?: string) => (store: RootState) =>
    store.project.companiesSet.data.find((companySet) => companySet.meta.path === companySetPath);

export default companySetSlice.reducer;
